import { useState } from "react";
import { Link } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { EmptyState } from "../../components/AsyncStates";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import { PageHeader } from "../../components/PageHeader";
import { StatePill } from "../../components/StatePill";
import { QueryStates } from "../../components/QueryStates";
import { DeployWizard } from "../deploy-wizard/DeployWizard";
import { DeployInFlightCard } from "../services/DeployInFlightCard";
import { formatRelativeTime, shortDigest } from "../services/deployFormat";
import { type MyServiceRow, useMyServiceRows } from "./useMyServiceRows";
import "./my-services.css";

/** The board's two-state health pill (IA slice 4, OperateServices board) — deliberately
 * narrower than `HealthStatePill` (which renders the raw per-cell vocabulary): this is a
 * per-SERVICE rollup, not a per-cell status, so it only ever reads "healthy," "degraded," or
 * "unknown" (a service with no health cell yet — never painted with the same alarm tone as
 * degraded; A-10). */
function ServiceHealthPill({ health }: { health: MyServiceRow["health"] }) {
  if (health === "healthy") return <StatePill label="Healthy" tone="positive" shape="dot" />;
  if (health === "degraded") return <StatePill label="Degraded" tone="negative" shape="triangle" />;
  return <StatePill label="No health data yet" tone="neutral" shape="ring" />;
}

function serviceHref(row: MyServiceRow): string {
  return `/projects/${row.project.id}/services/${row.service.id}`;
}

function buildColumns(): DataTableColumn<MyServiceRow>[] {
  return [
    {
      key: "service",
      header: "Service",
      cell: (row) => (
        <Link className="data-table__primary-link data-table__mono" href={serviceHref(row)}>
          {row.project.name} / {row.service.name}
        </Link>
      ),
      className: "data-table__primary",
    },
    {
      key: "environment",
      header: "Environment",
      cell: (row) =>
        row.environmentName ? (
          <EnvironmentBadge name={row.environmentName} envClass={row.environmentClass} />
        ) : (
          <span className="data-table__empty-cell">—</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => <ServiceHealthPill health={row.health} />,
    },
    {
      key: "revision",
      header: "Running revision",
      cell: (row) => {
        const dep = row.latestDeployment;
        if (!dep) return <span className="data-table__empty-cell">—</span>;
        return dep.digest ? shortDigest(dep.digest) : dep.image || "Unpinned";
      },
      className: (row) => (row.latestDeployment?.digest ? "data-table__mono" : "data-table__secondary"),
    },
    {
      key: "lastDeploy",
      header: "Last deploy",
      cell: (row) => {
        const dep = row.latestDeployment;
        if (!dep) return <span className="data-table__empty-cell">—</span>;
        return (
          <>
            <time dateTime={dep.createdAt} title={new Date(dep.createdAt).toLocaleString()}>
              {formatRelativeTime(dep.createdAt)}
            </time>
            {dep.createdBy && <> · {dep.createdBy}</>}
          </>
        );
      },
      className: "data-table__secondary",
    },
  ];
}

const COLUMNS = buildColumns();

/**
 * Operate landing (IA slice 4, OperateServices board,
 * docs/product/ui-information-architecture.md §3) — the App Developer's daily loop: find my
 * service, see whether it needs attention FIRST, then its deploy status/where it runs.
 * Absorbs the old "My services" (M2 PR-2, #245) — v0 ownership is still `Service.createdBy`
 * (no team entity, no membership; see `useMyServiceRows`'s doc comment for exactly which
 * session field this matches against and why).
 *
 * The attention banner (board: "surfaced first") renders one entry per DEGRADED row, above
 * the table — real health data (`useMyServiceRows`' rolled-up `health`/`healthDetail`/
 * counts), never a synthetic "something might be wrong" placeholder. A healthy or
 * not-yet-observed fleet shows no banner at all (A-10: no alarm without a real trouble
 * signal).
 *
 * IA slice 7: this page is now mounted inside `OperatePage`'s `TabPanel`, not at its own
 * top-level route. The section h1 ("Operate") and the tab bar live in the container; this
 * component provides only the Services tab content.
 */
function isDeployActive(row: MyServiceRow): boolean {
  const s = row.latestDeployment?.status;
  return s === "pending" || s === "deploying";
}

export function MyServicesPage() {
  const { session } = useAuth();
  const canDeploy = canManageFleet(session?.role);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { rows, isPending, isError, error, placementsDegraded, refetch } = useMyServiceRows();
  const degradedRows = rows.filter((row) => row.health === "degraded");
  const activeDeployRows = rows.filter(isDeployActive);

  return (
    <>
      <PageHeader
        title="My services"
        description="Your daily loop: services you created in this fleet."
        actions={canDeploy ? <Button variant="primary" onClick={() => setWizardOpen(true)}>Deploy</Button> : undefined}
      />

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading your services…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load your services. Check your connection and try again."
        }
        isEmpty={rows.length === 0}
        empty={
          <EmptyState
            title="No services here yet"
            description="Deploy a scaffold to get started, or develop a project and service by hand."
            action={
              canDeploy ? (
                <div className="my-services__empty-actions">
                  <Button variant="primary" onClick={() => setWizardOpen(true)}>
                    Deploy a scaffold
                  </Button>
                  <Link href="/projects" className="button button--secondary">
                    Develop a service
                  </Link>
                </div>
              ) : (
                <Link href="/projects" className="button button--primary">
                  Develop a service
                </Link>
              )
            }
          />
        }
      >
        {placementsDegraded && (
          <p className="my-services__warning" role="status">
            Couldn't load everything for every service (where it runs, health, or deploy history). Showing what
            loaded — try again in a moment.
          </p>
        )}

        {degradedRows.map((row) => (
          <Banner
            key={row.service.id}
            tone="danger"
            title={`${row.project.name} / ${row.service.name} needs attention`}
            className="my-services__attention-banner"
            action={
              <Link href={serviceHref(row)} className="button button--secondary">
                Investigate
              </Link>
            }
          >
            {row.healthUnhealthyCount > 0 && row.healthTotalCount > 0
              ? `Health check failing on ${row.healthUnhealthyCount} of ${row.healthTotalCount} machine${
                  row.healthTotalCount === 1 ? "" : "s"
                } it runs on.`
              : "Its health check is reporting trouble."}
            {row.healthDetail && ` ${row.healthDetail}.`}
          </Banner>
        ))}

        {activeDeployRows.length > 0 && (
          <div className="my-services__in-flight">
            <h3 className="my-services__in-flight-heading">Deploys in flight</h3>
            {activeDeployRows.map((row) => (
              <div key={row.service.id} className="my-services__in-flight-item">
                <Link href={serviceHref(row)} className="my-services__in-flight-label">
                  {row.project.name} / {row.service.name}
                </Link>
                <DeployInFlightCard
                  projectId={row.project.id}
                  serviceId={row.service.id}
                  active={row.latestDeployment!}
                />
              </div>
            ))}
          </div>
        )}

        <DataTable columns={COLUMNS} rows={rows} rowKey={(row) => row.service.id} />
      </QueryStates>

      {canDeploy && <DeployWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />}
    </>
  );
}
